import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import {
  Alert, Box, IconButton, Paper, Stack, Table, TableBody, TableCell, TableHead,
  TableRow, Tooltip, Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { Fragment, useState } from "react";
import { Link } from "react-router-dom";

import { api } from "../api/client";
import type { PortfolioReport, ProjectReport, ReportRow } from "../api/types";
import { formatMoney } from "../lib/money";
import { useOrganization } from "../lib/useOrganization";

const KOLUMNY = [
  { key: "budget", label: "Plan" },
  { key: "committed", label: "Zakontraktowane" },
  { key: "open_commitment", label: "Otwarte" },
  { key: "actual", label: "Rzeczywiste" },
  { key: "paid", label: "Zapłacone" },
] as const;

function Kwoty({ dane, mocno }: { dane: Record<string, string | null>; mocno?: boolean }) {
  return (
    <>
      {KOLUMNY.map((k) => (
        <TableCell key={k.key} align="right"
                   sx={{ fontVariantNumeric: "tabular-nums",
                         fontWeight: mocno ? 600 : undefined }}>
          {formatMoney(dane[k.key])}
        </TableCell>
      ))}
    </>
  );
}

/**
 * Pozycje kosztowe jednej produkcji, doczytywane dopiero po rozwinięciu.
 *
 * Raport pojedynczego projektu to kilka zapytań sumujących po alokacjach.
 * Pobieranie ich dla wszystkich produkcji przy wejściu na portfel oznaczałoby
 * kilkadziesiąt takich zapytań na coś, czego nikt jeszcze nie chciał zobaczyć.
 * Komponent montuje się przy rozwinięciu, więc leniwe pobranie wynika
 * z samej konstrukcji, a nie z dodatkowego przełącznika.
 */
function PozycjeProdukcji({ code }: { code: string }) {
  const { data, isPending } = useQuery({
    queryKey: ["report", code],
    queryFn: () => api.get<ProjectReport>(`/reports/four-columns/${code}`),
  });
  // Odwrotnie niż na ekranie raportu: tam projekt jest tematem strony
  // i drzewo zaczyna rozwinięte, tu rozwinięcie jednej produkcji na czterdzieści
  // wierszy zasłoniłoby portfel. Widać więc same kategorie topsheetu,
  // a szczegóły dokłada się kliknięciem.
  const [otwarteWezly, setOtwarteWezly] = useState<Set<string>>(new Set());

  if (isPending) {
    return (
      <TableRow>
        <TableCell colSpan={KOLUMNY.length + 1} sx={{ pl: 7 }}>
          <Typography variant="body2" color="text.secondary">Wczytuję pozycje…</Typography>
        </TableCell>
      </TableRow>
    );
  }
  if (!data || data.rows.length === 0) {
    return (
      <TableRow>
        <TableCell colSpan={KOLUMNY.length + 1} sx={{ pl: 7 }}>
          <Typography variant="body2" color="text.secondary">
            Ta produkcja nie ma jeszcze pozycji kosztorysu.
          </Typography>
        </TableCell>
      </TableRow>
    );
  }

  function przelacz(id: string) {
    setOtwarteWezly((s) => {
      const kopia = new Set(s);
      if (kopia.has(id)) kopia.delete(id);
      else kopia.add(id);
      return kopia;
    });
  }

  function wiersze(rows: ReportRow[]): React.ReactNode[] {
    return rows.flatMap((row) => {
      const maDzieci = row.children.length > 0;
      const rozwiniety = otwarteWezly.has(row.account_code_id);
      const element = (
        <TableRow key={row.account_code_id} hover
                  onClick={maDzieci ? () => przelacz(row.account_code_id) : undefined}
                  sx={{ cursor: maDzieci ? "pointer" : undefined }}>
          <TableCell sx={{ pl: 6 + row.depth * 2.5, py: 0.25, border: 0 }}>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              {maDzieci ? (
                <IconButton size="small"
                            aria-label={rozwiniety ? "Zwiń pozycje" : "Rozwiń pozycje"}
                            onClick={(e) => { e.stopPropagation();
                                              przelacz(row.account_code_id); }}>
                  {rozwiniety ? <KeyboardArrowDownIcon fontSize="small" />
                              : <KeyboardArrowRightIcon fontSize="small" />}
                </IconButton>
              ) : <Box sx={{ width: 30 }} />}
              <Typography variant="body2" color="text.secondary">
                {row.code} {row.name}
              </Typography>
            </Stack>
          </TableCell>
          <Kwoty dane={row as unknown as Record<string, string | null>} />
        </TableRow>
      );
      return maDzieci && rozwiniety ? [element, ...wiersze(row.children)] : [element];
    });
  }

  return <>{wiersze(data.rows)}</>;
}

export function PortfolioPage() {
  const [otwarte, setOtwarte] = useState<Set<string>>(new Set());
  const { organization } = useOrganization();
  // Zawężenie liczy serwer, a nie ten ekran: sumy per podmiot i suma zbiorcza
  // muszą pochodzić z tego samego przebiegu co wiersze, inaczej odfiltrowana
  // spółka zostawałaby w podsumowaniu.
  const { data } = useQuery({
    queryKey: ["portfolio", organization],
    queryFn: () =>
      api.get<PortfolioReport>(
        `/reports/four-columns${
          organization ? `?organization=${encodeURIComponent(organization)}` : ""
        }`,
      ),
  });

  if (!data) return null;
  const wieleSpolek = data.organizations.length > 1;

  function przelaczProdukcje(kod: string) {
    setOtwarte((s) => {
      const kopia = new Set(s);
      if (kopia.has(kod)) kopia.delete(kod);
      else kopia.add(kod);
      return kopia;
    });
  }

  return (
    <Stack spacing={2}>
      <Typography variant="h6">Portfel produkcji</Typography>

      <Paper variant="outlined" sx={{ overflowX: "auto" }}>
        <Table size="small" sx={{ minWidth: 900 }}>
          <TableHead>
            <TableRow>
              <TableCell>Produkcja</TableCell>
              {KOLUMNY.map((k) => (
                <TableCell key={k.key} align="right">
                  {k.key === "paid" && !data.paid_source_connected ? (
                    <Tooltip title="Źródło (InsERT) niepodłączone — etap 3.">
                      <span style={{ opacity: 0.5 }}>{k.label}</span>
                    </Tooltip>
                  ) : k.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.organizations.map((grupa) => (
              <Fragment key={grupa.organization.id}>
                <TableRow sx={{ bgcolor: "action.hover" }}>
                  <TableCell colSpan={KOLUMNY.length + 1}>
                    <Typography variant="body2" fontWeight={600}>
                      {grupa.organization.name}
                    </Typography>
                  </TableCell>
                </TableRow>

                {grupa.projects.map((p) => {
                  const rozwiniety = otwarte.has(p.project.code);
                  return (
                    <Fragment key={p.project.id}>
                      <TableRow hover sx={{ cursor: "pointer" }}
                                onClick={() => przelaczProdukcje(p.project.code)}>
                        <TableCell sx={{ pl: 3 }}>
                          <Stack direction="row" alignItems="center" spacing={0.5}>
                            <IconButton
                              size="small"
                              aria-label={rozwiniety
                                ? "Zwiń pozycje kosztowe" : "Rozwiń pozycje kosztowe"}
                              onClick={(e) => { e.stopPropagation();
                                                przelaczProdukcje(p.project.code); }}
                            >
                              {rozwiniety ? <KeyboardArrowDownIcon fontSize="small" />
                                          : <KeyboardArrowRightIcon fontSize="small" />}
                            </IconButton>
                            <Box>
                              {/* Odsyłacz zatrzymuje kliknięcie: przejście
                                  do pełnego raportu i rozwinięcie podglądu
                                  to dwie różne intencje w tym samym miejscu. */}
                              <Link to={`/projekty/${p.project.code}/raport`}
                                    onClick={(e) => e.stopPropagation()}>
                                {p.project.code}
                              </Link>{" "}
                              {p.project.name}
                              {Number(p.unallocated_actual) > 0 && (
                                <Typography variant="caption" color="warning.main"
                                            display="block">
                                  bez przypisania: {formatMoney(p.unallocated_actual)} zł
                                </Typography>
                              )}
                            </Box>
                          </Stack>
                        </TableCell>
                        <Kwoty dane={p.totals as unknown as Record<string, string | null>} />
                      </TableRow>
                      {rozwiniety && <PozycjeProdukcji code={p.project.code} />}
                    </Fragment>
                  );
                })}

                {/* Sumy per podmiot są liczbami wiążącymi — każda spółka
                    rozlicza się osobno. */}
                <TableRow>
                  <TableCell sx={{ pl: 3 }}>
                    <Typography variant="body2" fontWeight={600}>
                      Razem w podmiocie
                    </Typography>
                  </TableCell>
                  <Kwoty dane={grupa.totals as unknown as Record<string, string | null>}
                         mocno />
                </TableRow>
              </Fragment>
            ))}

            {wieleSpolek && (
              <TableRow sx={{ "& td": { borderTop: 2 } }}>
                <TableCell>
                  <Typography variant="body2" fontWeight={600}>
                    Suma wszystkich podmiotów
                  </Typography>
                </TableCell>
                <Kwoty dane={data.grand_totals as unknown as Record<string, string | null>}
                       mocno />
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      {wieleSpolek && (
        <Alert severity="info">
          {data.grand_totals_note} Liczbami, którymi wolno się posłużyć, są sumy
          per podmiot.
        </Alert>
      )}

      <Box />
    </Stack>
  );
}
